import axios from 'axios';
import { FleetSummaryItem, VesselDetails } from './_models';

const API_URL = process.env.REACT_APP_API_URL

const VESSEL_API_URL = `${API_URL}/vessels`;
const DASHBOARD_API_URL = `${API_URL}/dashboard`;

// ─────────────────────────────────────────────────────────────
// Fleet Summary — single call that populates the vessel list
// and map markers on the Home dashboard.
// ─────────────────────────────────────────────────────────────

/**
 * Returns compact fleet summary scoped to the authenticated user.
 * Use this to render the vessel list and map markers.
 * Does NOT return full vessel details (master, CE, agent, draft, etc.).
 */
export const getFleetSummary = async (): Promise<FleetSummaryItem[]> => {
  try {
    const response = await axios.get<FleetSummaryItem[]>(`${DASHBOARD_API_URL}/fleet-summary`);
    return response.data ?? [];
  } catch (error) {
    console.error('[getFleetSummary] Failed to fetch fleet summary:', error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────
// Vessel detail — loaded lazily when the user selects a vessel.
// Results are cached in memory for the lifetime of the page
// session so revisiting the same marker makes zero extra calls.
// ─────────────────────────────────────────────────────────────

/** In-memory cache: vesselId → VesselDetails (fulfilled only) */
const detailCache = new Map<number, VesselDetails>();
/** In-flight promise cache: prevents duplicate concurrent requests */
const inFlight = new Map<number, Promise<VesselDetails | undefined>>();

/** Check if a space-separated DMS string has valid component ranges */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _isValidDms = (raw: string): boolean => {
  const m = String(raw ?? '').trim().match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s+([NSEW])$/i)
  if (!m) return true
  const min = parseFloat(m[2])
  const sec = parseFloat(m[3])
  return min < 60 && sec < 60
}

/**
 * Fetches full vessel detail from /api/vessels/landing/{vesselId}.
 * Results are cached by vesselId — subsequent calls return immediately.
 * Call this ONLY when a user clicks a map marker or vessel list item.
 */
export const getVesselDetails = async (vesselId: number): Promise<VesselDetails | undefined> => {
  // Return from cache if already fetched
  if (detailCache.has(vesselId)) {
    return detailCache.get(vesselId);
  }

  // Deduplicate concurrent requests for the same vesselId
  if (inFlight.has(vesselId)) {
    return inFlight.get(vesselId);
  }

  const request = fetchVesselDetailsFromApi(vesselId).then((result) => {
    inFlight.delete(vesselId);
    if (result) detailCache.set(vesselId, result);
    return result;
  });

  inFlight.set(vesselId, request);
  return request;
};

/** Clears the detail cache (e.g. on logout or manual refresh). */
export const clearVesselDetailCache = () => {
  detailCache.clear();
  inFlight.clear();
};

/** Internal: performs the actual HTTP fetch and parses the response. */
const fetchVesselDetailsFromApi = async (vesselId: number): Promise<VesselDetails | undefined> => {
  try {
    const response = await axios.get(`${VESSEL_API_URL}/landing/${vesselId}`);
    const raw = response.data;
    const vesselDto = raw.vesseldto || {};
    const voyage = raw.latestVoyage || {};

    // Resolve latitude/longitude — check top-level, vesseldto, latestVoyage, and position fields
    const rawLat = raw.latitude != null && raw.latitude !== ''
      ? String(raw.latitude)
      : vesselDto.latitude != null && vesselDto.latitude !== ''
        ? String(vesselDto.latitude)
        : raw.positionLat != null && raw.positionLat !== ''
          ? String(raw.positionLat)
          : raw.lat != null && raw.lat !== ''
            ? String(raw.lat)
            : raw.position_lat != null && raw.position_lat !== ''
              ? String(raw.position_lat)
              : voyage.latitude != null && voyage.latitude !== ''
                ? String(voyage.latitude)
                : voyage.positionLat != null && voyage.positionLat !== ''
                  ? String(voyage.positionLat)
                  : '';

    const rawLng = raw.longitude != null && raw.longitude !== ''
      ? String(raw.longitude)
      : vesselDto.longitude != null && vesselDto.longitude !== ''
        ? String(vesselDto.longitude)
        : raw.positionLon != null && raw.positionLon !== ''
          ? String(raw.positionLon)
          : raw.lon != null && raw.lon !== ''
            ? String(raw.lon)
            : raw.lng != null && raw.lng !== ''
              ? String(raw.lng)
              : raw.position_lon != null && raw.position_lon !== ''
                ? String(raw.position_lon)
                : voyage.longitude != null && voyage.longitude !== ''
                  ? String(voyage.longitude)
                  : voyage.positionLon != null && voyage.positionLon !== ''
                    ? String(voyage.positionLon)
                    : '';

    const result: VesselDetails = {
      fleet_name: vesselDto.fleet_name || '',
      imoNumber: vesselDto.imoNumber || '',
      mmsi: vesselDto.mmsi || raw.mmsi,
      call_sign: vesselDto.call_sign || raw.call_sign,
      flag: vesselDto.flag || raw.flag,
      classes: vesselDto.classes || raw.classes,
      area: vesselDto.area || raw.area,
      dwt: vesselDto.dwt || raw.dwt,
      latitude: rawLat,
      longitude: rawLng,
      lastPort: raw.lastPort || '',
      atd: raw.atd || '',
      nextPort: raw.nextPort || '',
      eta: raw.eta || '',
      status: raw.status || '',
      latLongSource: raw.latLongSource || '',
      lastUpdated: raw.lastUpdated || '',
      course: raw.course || '',
      speed: raw.speed || '',
      cargoWeight: raw.cargoWeight || '',
      distanceObserved: raw.distanceObserved || '',
      masterName: raw.masterName || '',
      masterContactNumber: raw.masterContactNumber || '',
      masterEmail: raw.masterEmail || '',
      chiefEngineerName: raw.chiefEngineerName || '',
      chiefEngineerContactNumber: raw.chiefEngineerContactNumber || '',
      chiefEngineerEmail: raw.chiefEngineerEmail || '',
      agentName: voyage.agentName || '',
      agentContactNumber: voyage.agentContactNumber || '',
      agentTelephoneNumber: voyage.agentTelephoneNumber || '',
      agentFaxAddress: voyage.agentFaxAddress || '',
      agentEmail: voyage.agentEmail || '',
      draftFore: voyage.draftFore || '',
      draftMid: voyage.draftMid || '',
      draftAft: voyage.draftAft || '',
      latestVoyage: voyage,
    };

    return result;
  } catch (error: any) {
    console.error('[getVesselDetails] Failed to fetch details for vessel ' + vesselId + ':', error);
    return undefined;
  }
};

/* ──────────────────────────────────────────────────────────────────
   Home Dashboard — aggregated / derived-data endpoints
   ────────────────────────────────────────────────────────────────── */

const PMS_API_URL = `${API_URL}/pms`

/**
 * GET /pms/command-center-stats
 * Fleet-wide health statistics for the home dashboard KPI bar.
 */
export const getPmsCommandCenterStats = async (cgaId?: number): Promise<any> => {
  const params: Record<string, string | number> = {}
  if (cgaId) params.companyId = cgaId
  const { data } = await axios.get(`${PMS_API_URL}/command-center-stats`, { params })
  return data
}

/**
 * GET /pms/jobs/search?state=OVERDUE
 * Overdue PMS jobs displayed in the Work Queue / Insights.
 */
export const getPmsOverdueJobs = async (cgaId?: number, vesselId?: number): Promise<any[]> => {
  const params: Record<string, string | number> = { state: 'OVERDUE' }
  if (cgaId) params.companyId = cgaId
  if (vesselId) params.vesselId = vesselId
  const { data } = await axios.get(`${PMS_API_URL}/jobs`, { params })
  return data.content || data || []
}

/**
 * GET /pms/calendar-events?from=...&to=...
 * Calendar events for the 7-day scheduler timeline.
 */
export const getPmsCalendarEvents = async (cgaId?: number, days = 14): Promise<any[]> => {
  const now = new Date()
  const to = new Date(now.getTime() + days * 24 * 60 * 60 * 1000)
  const params: Record<string, string | number | Date> = {
    from: now.toISOString(),
    to: to.toISOString(),
  }
  if (cgaId) params.companyId = cgaId
  const { data } = await axios.get(`${PMS_API_URL}/calendar-events`, { params })
  return data.content || data || []
}

/**
 * GET /events/upcoming-critical
 * Upcoming critical events (audits, surveys, dry-docks etc.) for the dashboard.
 */
export const getUpcomingCriticalEvents = async (cgaId?: number): Promise<any[]> => {
  const params: Record<string, string | number> = {}
  if (cgaId) params.companyId = cgaId
  const { data } = await axios.get(`${API_URL}/events/upcoming-critical`, { params })
  return data
}
